import { useState } from "react"
import {
    Box,
    Button,
    Card,
    CardContent,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Stack,
    Switch,
    Typography,
} from "@mui/material"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import { SortableItem } from "lib/@components/SortableItem"
import Iconify from "minimals-template/components/Iconify"
import { DragHandle } from "lib/dragHandle"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { RuleConditionsList } from "./rule-conditions-list"
import { RuleActionSelector } from "./rule-action-selector"
import { RuleJustificationField } from "./rule-justification-field"
import { formatRuleSummary, formatActionLabel } from "./format-rule-summary"

export function RuleCard({ item, onUpdate, onRemove, onDuplicate, readOnly }) {
    const rule = item
    const { conditionFields = [] } = useBoundContext()
    const [dragProps, setDragProps] = useState({})
    const [editing, setEditing] = useState(shouldStartEditing)
    const [menuAnchor, setMenuAnchor] = useState(null)
    const [draft, setDraft] = useState(() => cloneRule(rule))

    const enabled = rule.enabled !== false
    const summary = formatRuleSummary(rule, conditionFields)
    const actionLabel = formatActionLabel(rule.action)
    const actionColor = rule.action?.type === "exclude" ? "error.main" : "primary.main"
    const summaryOpacity = getSummaryOpacity()

    return (
        <SortableItem id={rule.id} setDragProps={setDragProps} disabled={readOnly}>
            <If value={editing}>
                <Card variant="outlined" sx={{ mb: 1 }} data-cy="rule-card-editing">
                    <CardContent sx={{ "&:last-child": { pb: 2 } }}>
                        <Stack spacing={2}>
                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    color="text.primary"
                                    fontWeight={700}
                                    sx={{ mb: 1.5, display: "block", fontSize: 16 }}
                                >
                                    Conditions (If)
                                </Typography>
                                <RuleConditionsList
                                    conditions={draft.conditions || []}
                                    combinator={draft.combinator || "and"}
                                    onUpdate={handleDraftConditions}
                                    onCombinatorChange={handleDraftCombinator}
                                />
                            </Box>
                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    color="text.primary"
                                    fontWeight={700}
                                    sx={{ mb: 1.5, display: "block", fontSize: 16 }}
                                >
                                    Action
                                </Typography>
                                <RuleActionSelector
                                    action={draft.action || { type: "include" }}
                                    onUpdate={handleDraftAction}
                                />
                            </Box>
                            <RuleJustificationField value={draft.justification} onChange={handleDraftJustification} />
                            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ pt: 1 }}>
                                <Button onClick={handleSave} variant="contained" data-cy="rule-save">
                                    Save Rule
                                </Button>
                                <Button onClick={handleCancel} variant="text">
                                    Cancel
                                </Button>
                            </Stack>
                        </Stack>
                    </CardContent>
                </Card>
                <Else>
                    <Card variant="outlined" sx={{ mb: 1 }} data-cy="rule-card-summary">
                        <CardContent
                            sx={{
                                py: 2.5,
                                "&:last-child": { pb: 2.5 },
                                opacity: summaryOpacity,
                            }}
                        >
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <If value={!readOnly}>
                                    <DragHandle {...dragProps} />
                                </If>
                                <Typography variant="body1" sx={{ flex: 1 }}>
                                    <Box component="span" color="text.secondary">
                                        If{" "}
                                    </Box>
                                    <Box component="span" fontWeight={600}>
                                        {summary}
                                    </Box>{" "}
                                    <Box component="span" sx={{ color: actionColor, fontWeight: 600 }}>
                                        {actionLabel}
                                    </Box>
                                </Typography>
                                <If value={!readOnly}>
                                    <Switch
                                        checked={enabled}
                                        onChange={handleToggleEnabled}
                                        data-cy="rule-enabled-toggle"
                                    />
                                    <IconButton
                                        aria-label="Edit rule"
                                        onClick={handleEdit}
                                        data-cy="rule-edit"
                                        sx={{ p: 0.5 }}
                                    >
                                        <Iconify icon="solar:pen-bold" width={20} />
                                    </IconButton>
                                    <IconButton
                                        aria-label="More options"
                                        size="small"
                                        onClick={openMenu}
                                        data-cy="rule-menu"
                                    >
                                        <Iconify icon="eva:more-vertical-fill" width={20} />
                                    </IconButton>
                                </If>
                            </Stack>
                        </CardContent>
                    </Card>
                </Else>
            </If>
            <If value={!readOnly}>
                <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
                    <MenuItem onClick={handleEditFromMenu}>
                        <ListItemIcon>
                            <Iconify icon="solar:pen-bold" width={20} />
                        </ListItemIcon>
                        <ListItemText>Edit</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleDuplicateFromMenu}>
                        <ListItemIcon>
                            <Iconify icon="solar:copy-bold" width={20} />
                        </ListItemIcon>
                        <ListItemText>Duplicate</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleRemoveFromMenu}>
                        <ListItemIcon>
                            <Iconify icon="mdi:delete-outline" width={20} />
                        </ListItemIcon>
                        <ListItemText>Remove</ListItemText>
                    </MenuItem>
                </Menu>
            </If>
        </SortableItem>
    )

    function handleEdit() {
        if (readOnly) return
        setDraft(cloneRule(rule))
        setEditing(true)
    }

    function handleCancel() {
        if (readOnly) return
        if (isEmptyRule(rule)) {
            onRemove(rule.id)
            return
        }
        setEditing(false)
    }

    function handleSave() {
        if (readOnly) return
        onUpdate(rule.id, {
            conditions: draft.conditions || [],
            combinator: draft.combinator || "and",
            action: draft.action || { type: "include" },
            justification: draft.justification || "",
        })
        setEditing(false)
    }

    function handleDraftConditions(conditions) {
        setDraft((prev) => ({ ...prev, conditions }))
    }

    function handleDraftJustification(justification) {
        setDraft((prev) => ({ ...prev, justification }))
    }

    function handleDraftCombinator(combinator) {
        setDraft((prev) => ({ ...prev, combinator }))
    }

    function handleDraftAction(action) {
        setDraft((prev) => ({ ...prev, action }))
    }

    function handleToggleEnabled(e) {
        if (readOnly) return
        onUpdate(rule.id, { enabled: e.target.checked })
    }

    function openMenu(e) {
        if (readOnly) return
        setMenuAnchor(e.currentTarget)
    }

    function closeMenu() {
        setMenuAnchor(null)
    }

    function handleEditFromMenu() {
        closeMenu()
        handleEdit()
    }

    function handleDuplicateFromMenu() {
        closeMenu()
        onDuplicate?.(rule)
    }

    function handleRemoveFromMenu() {
        closeMenu()
        onRemove(rule.id)
    }

    function shouldStartEditing() {
        if (readOnly) return false
        return isEmptyRule(rule)
    }

    function getSummaryOpacity() {
        if (enabled) return 1
        return 0.5
    }
}

function isEmptyRule(rule) {
    const conditions = rule?.conditions || []
    if (conditions.length === 0) return true
    return conditions.every((c) => !c.field)
}

function cloneRule(rule) {
    return {
        ...rule,
        combinator: rule?.combinator === "or" ? "or" : "and",
        conditions: (rule.conditions || []).map((c) => ({ ...c })),
        action: rule.action ? { ...rule.action } : { type: "include" },
    }
}
